import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import SectionThemeTagger from '../../components/strategy/SectionThemeTagger';
import SectionRoleAssigner from '../../components/pursuit/SectionRoleAssigner';
import OutlineBuilder from '../../components/pursuit/OutlineBuilder';
import SectionDocumentVersions from '../../components/pursuit/SectionDocumentVersions';
import VolumeReviewPanel from '../../components/pursuit/VolumeReviewPanel';
import VolumeReviewCycle from '../../components/pursuit/VolumeReviewCycle';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { SavingIndicator } from '../../components/ui/SavingIndicator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import NotificationBell from '../../components/pursuit/NotificationBell';
import { toast } from 'sonner';
import { 
  ChevronRight,
  ChevronDown,
  FileText,
  Edit3,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Save,
  Clock,
  CheckCircle2,
  AlertCircle,
  FolderOpen,
  Folder,
  File,
  ListChecks,
  Lightbulb,
  Sparkles,
  Settings,
  PencilLine,
  Eye,
  Info,
  Award,
  AlertTriangle,
  Type,
  Layout,
  FileType,
  Copy,
  UserCog,
  CalendarClock,
  Upload
} from 'lucide-react';
import { cn } from '../../lib/utils';
import PursuitLifecycleStepper from '../../components/pursuit/PursuitLifecycleStepper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Volume icons and colors
const VOLUME_CONFIG = {
  'I': { icon: FileText, color: 'blue', label: 'Technical' },
  'II': { icon: Award, color: 'amber', label: 'Past Perf' },
  'III': { icon: FileType, color: 'emerald', label: 'Cost' },
  'IV': { icon: ListChecks, color: 'purple', label: 'Admin' }
};

// Status badge configurations
const STATUS_CONFIG = {
  'NOT_STARTED': { label: 'Not Started', color: 'bg-slate-100 text-slate-600', icon: File },
  'DRAFTING': { label: 'Drafting', color: 'bg-blue-100 text-blue-700', icon: PencilLine },
  'IN_REVIEW': { label: 'In Review', color: 'bg-amber-100 text-amber-700', icon: Eye },
  'COMPLIANT': { label: 'Compliant', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 }
};

export default function ProposalWorkspaceLive() {
  const { workspaceId, pursuitId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  // Core State
  const [pursuit, setPursuit] = useState(null);
  const [structure, setStructure] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Selection State
  const [selectedVolume, setSelectedVolume] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [expandedVolumes, setExpandedVolumes] = useState({ 'I': true });
  
  // Editor State
  const [editorContent, setEditorContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  
  // Architect Features State
  const [draftShells, setDraftShells] = useState([]);
  const [winThemes, setWinThemes] = useState([]);
  const [showRequirements, setShowRequirements] = useState(false);
  const [showWinThemes, setShowWinThemes] = useState(false);
  const [showDocVersions, setShowDocVersions] = useState(true);
  const [pageLimitsDialog, setPageLimitsDialog] = useState(false);
  const [manualPageLimits, setManualPageLimits] = useState({});
  
  // Source Recommendations State (Similarity Matches from Group 5)
  const [sourceRecommendations, setSourceRecommendations] = useState([]);
  const [showRecommendations, setShowRecommendations] = useState(false);

  // Proposal Architect — Book Boss / Writer / Reviewer / Outline / Reviews
  const [roleAssignments, setRoleAssignments] = useState({}); // section_id -> {WRITER, REVIEWER}
  const [outlineBuilderVolume, setOutlineBuilderVolume] = useState(null); // {id, label} | null
  const [bookBossDialogVolume, setBookBossDialogVolume] = useState(null); // {id, label} | null
  const [bookBossDraft, setBookBossDraft] = useState({ name: '', email: '' });
  const [reviewPanelVolume, setReviewPanelVolume] = useState(null); // {id, label, sections} | null
  
  // Refs
  const autoSaveTimerRef = useRef(null);
  const lastContentRef = useRef('');
  const editorRef = useRef(null);

  // Fetch proposal structure from Architect endpoint
  const fetchStructure = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/proposal-structure`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setStructure(data);
        // Set manual page limits from pursuit if they exist
        if (data.volume_page_limits) {
          setManualPageLimits(data.volume_page_limits);
        }
      }
    } catch (err) {
      console.error('Failed to fetch structure:', err);
    }
  }, [pursuitId, getAuthHeaders]);

  // Fetch pursuit details
  const fetchPursuit = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setPursuit(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch pursuit:', err);
    }
  }, [pursuitId, getAuthHeaders]);

  // Fetch Writer/Reviewer assignments for every section in this pursuit,
  // indexed by section_id so the tree can render chips without an extra
  // round-trip per section.
  const fetchRoleAssignments = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/proposal-architect/role-assignments`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        const bySection = {};
        (data.assignments || []).forEach((a) => {
          if (!a.section_id) return;
          bySection[a.section_id] = { ...(bySection[a.section_id] || {}), [a.role]: a };
        });
        setRoleAssignments(bySection);
      }
    } catch (err) {
      // Non-fatal — legacy (non-Architect) pursuits have no role assignments.
    }
  }, [pursuitId, getAuthHeaders]);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchPursuit(), fetchStructure(), fetchSourceRecommendations(), fetchRoleAssignments()]);
      setIsLoading(false);
    };
    if (pursuitId) loadData();
  }, [pursuitId, fetchPursuit, fetchStructure, fetchRoleAssignments]);
  
  // Fetch Source Recommendations (Similarity Matches)
  const fetchSourceRecommendations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pursuit-kb/${pursuitId}/recommendations`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        // Filter for matches with similarity > 30%
        const filtered = (data.recommendations || []).filter(r => 
          r.similarity_score && r.similarity_score >= 0.30
        );
        setSourceRecommendations(filtered);
      }
    } catch (err) {
      console.error('Failed to fetch recommendations:', err);
    }
  };

  // Fetch draft shells when section changes
  const fetchDraftShells = async (sectionId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/draft-shells/${encodeURIComponent(sectionId)}`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setDraftShells(data.draft_shells || []);
      }
    } catch (err) {
      console.error('Failed to fetch draft shells:', err);
    }
  };

  // Fetch win themes when section changes
  const fetchWinThemes = async (sectionId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/win-themes/${encodeURIComponent(sectionId)}`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setWinThemes(data.win_themes || []);
      }
    } catch (err) {
      console.error('Failed to fetch win themes:', err);
    }
  };

  // Fetch existing draft content for section
  const fetchDraft = async (sectionId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/proposal-drafts/${encodeURIComponent(sectionId)}`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        return data;
      }
    } catch (err) {
      console.error('Failed to fetch draft:', err);
    }
    return null;
  };

  // Open the section a finding refers to, closing the reviews dialog so the
  // writer lands in the editor. A finding you have to go hunting for is a note;
  // one that takes you to the text is actionable.
  const jumpToSection = async (sectionId) => {
    if (!sectionId || !structure?.volumes) return;
    // `structure.volumes` is keyed by volume number (I/II/III), not an array —
    // the same key `expandedVolumes` uses.
    for (const [volNum, volume] of Object.entries(structure.volumes)) {
      const section = (volume.sections || []).find((s) => s.section_id === sectionId);
      if (section) {
        setReviewPanelVolume(null);
        setExpandedVolumes((prev) => ({ ...prev, [volNum]: true }));
        await handleSelectSection(volume, section);
        return;
      }
    }
    // Sections get renamed and deleted during recovery; frozen findings can
    // outlive the section they point at. Say so rather than doing nothing.
    toast.error('That section no longer exists in the outline.');
  };

  // Handle section selection
  const handleSelectSection = async (volume, section) => {
    // Save current content before switching
    if (selectedSection && editorContent !== lastContentRef.current) {
      await saveDraft(selectedSection.section_id, editorContent);
    }
    
    setSelectedVolume(volume);
    setSelectedSection(section);
    
    // Load draft content
    const draft = await fetchDraft(section.section_id);
    if (draft) {
      setEditorContent(draft.content || '');
      setWordCount(draft.word_count || 0);
      lastContentRef.current = draft.content || '';
    } else {
      setEditorContent('');
      setWordCount(0);
      lastContentRef.current = '';
    }
    
    setLastSaved(null);
    
    // Fetch Architect data
    fetchDraftShells(section.section_id);
    fetchWinThemes(section.section_id);
  };

  // Save draft
  const saveDraft = async (sectionId, content) => {
    if (!sectionId) return;
    
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/proposal-drafts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          section_id: sectionId,
          content: content,
          content_format: 'MARKDOWN'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setLastSaved(data.saved_at);
        setWordCount(data.word_count);
        lastContentRef.current = content;
        
        // Refresh structure to update stats
        fetchStructure();
        toast.success('Draft saved');
      }
    } catch (err) {
      console.error('Failed to save draft:', err);
      toast.error('Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle editor change with auto-save
  const handleEditorChange = (value) => {
    setEditorContent(value);
    setWordCount(value.trim() ? value.trim().split(/\s+/).length : 0);
    
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(() => {
      if (selectedSection && value !== lastContentRef.current) {
        saveDraft(selectedSection.section_id, value);
      }
    }, 30000);
  };

  // Manual save
  const handleManualSave = () => {
    if (selectedSection) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      saveDraft(selectedSection.section_id, editorContent);
    }
  };

  // Insert ghost text at cursor
  const insertGhostText = (text) => {
    const textarea = editorRef.current;
    if (!textarea) {
      setEditorContent(prev => prev + '\n\n' + text);
      return;
    }
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent = editorContent.substring(0, start) + text + editorContent.substring(end);
    setEditorContent(newContent);
    handleEditorChange(newContent);
    
    // Focus and set cursor after inserted text
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };

  // Update draft status
  const updateDraftStatus = async (sectionId, status) => {
    try {
      await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/proposal-drafts/${encodeURIComponent(sectionId)}/status?status=${status}`,
        { method: 'PATCH', headers: getAuthHeaders() }
      );
      fetchStructure();
      toast.success(`Status updated to ${status}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Save page limits
  const savePageLimits = async () => {
    try {
      await fetch(`${API_URL}/api/pursuits/${pursuitId}/page-limits`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(manualPageLimits)
      });
      setPageLimitsDialog(false);
      fetchStructure();
      toast.success('Page limits updated');
    } catch (err) {
      toast.error('Failed to save page limits');
    }
  };

  // Assign Book Boss for a real (Proposal Architect) volume
  const saveBookBoss = async () => {
    if (!bookBossDialogVolume) return;
    if (!bookBossDraft.name.trim()) {
      toast.error('Enter a name');
      return;
    }
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/submittal-volumes/${bookBossDialogVolume.id}`,
        {
          method: 'PATCH',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: bookBossDraft.name.trim(), email: bookBossDraft.email.trim() || null }),
        }
      );
      if (!res.ok) throw new Error();
      toast.success('Book Boss assigned');
      setBookBossDialogVolume(null);
      fetchStructure();
    } catch {
      toast.error('Failed to assign Book Boss');
    }
  };

  // Toggle volume expansion
  const toggleVolume = (volumeNumber) => {
    setExpandedVolumes(prev => ({
      ...prev,
      [volumeNumber]: !prev[volumeNumber]
    }));
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Calculate overall stats
  const getStats = () => {
    if (!structure) return { totalSections: 0, sectionsWithContent: 0, totalRequirements: 0, progress: 0 };
    
    let totalSections = 0;
    let sectionsWithContent = 0;
    
    Object.values(structure.volumes || {}).forEach(vol => {
      totalSections += vol.sections?.length || 0;
      sectionsWithContent += vol.sections_with_content || 0;
    });
    
    return {
      totalSections,
      sectionsWithContent,
      totalRequirements: structure.total_requirements || 0,
      progress: totalSections > 0 ? Math.round((sectionsWithContent / totalSections) * 100) : 0
    };
  };

  const stats = getStats();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
          <p className="text-slate-400">Loading Proposal Architect...</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div data-testid="proposal-workspace-live" className="min-h-screen bg-slate-950 text-slate-100">
        {/* Header — v3.18.20 px-8 + max-w-7xl centering to match other pursuit screens */}
        <div className="bg-slate-900 border-b border-slate-800 px-8 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-400 mb-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                  onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/compliance`)}
                >
                  <ArrowLeft className="w-3 h-3 mr-1" />
                  Compliance
                </Button>
                <ChevronRight className="w-4 h-4" />
                <span className="text-blue-400 font-medium">Proposal Architect</span>
              </div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Proposal Architect Workspace
              </h1>
              {pursuit && (
                <p className="text-sm text-slate-400 mt-1">
                  {pursuit.opportunity_name}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Formatting Lock Button */}
              <Dialog open={pageLimitsDialog} onOpenChange={setPageLimitsDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Settings className="w-4 h-4 mr-2" />
                    Page Limits
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <DialogHeader>
                    <DialogTitle className="text-white">Volume Page Limits</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <p className="text-sm text-slate-400">
                      Override AI-extracted limits if an amendment changes them.
                    </p>
                    {['I', 'II', 'III', 'IV'].map(vol => (
                      <div key={vol} className="flex items-center justify-between">
                        <Label className="text-slate-300">Volume {vol}</Label>
                        <Input
                          type="number"
                          placeholder="No limit"
                          value={manualPageLimits[`Volume ${vol}`] || ''}
                          onChange={(e) => setManualPageLimits(prev => ({
                            ...prev,
                            [`Volume ${vol}`]: e.target.value ? parseInt(e.target.value) : null
                          }))}
                          className="w-24 bg-slate-800 border-slate-700 text-slate-100"
                        />
                      </div>
                    ))}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setPageLimitsDialog(false)} className="border-slate-700">
                      Cancel
                    </Button>
                    <Button onClick={savePageLimits} className="bg-blue-600 hover:bg-blue-700">
                      Save Limits
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              <NotificationBell
                pursuitId={pursuitId}
                onViewRedFlags={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/compliance?tab=submission-day`)}
              />
              {selectedSection && (
                <SavingIndicator
                  isSaving={isSaving}
                  lastSaved={lastSaved}
                  error={null}
                />
              )}
            </div>
          </div>

          {/* Book Boss assignment dialog */}
          <Dialog open={!!bookBossDialogVolume} onOpenChange={(v) => !v && setBookBossDialogVolume(null)}>
            <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-sm">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <UserCog className="w-4 h-4 text-violet-400" />
                  Book Boss — {bookBossDialogVolume?.label}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-slate-300 text-xs">Name</Label>
                  <Input
                    value={bookBossDraft.name}
                    onChange={(e) => setBookBossDraft((p) => ({ ...p, name: e.target.value }))}
                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Email (optional)</Label>
                  <Input
                    value={bookBossDraft.email}
                    onChange={(e) => setBookBossDraft((p) => ({ ...p, email: e.target.value }))}
                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBookBossDialogVolume(null)} className="border-slate-700">
                  Cancel
                </Button>
                <Button onClick={saveBookBoss} className="bg-violet-600 hover:bg-violet-700" data-testid="book-boss-save-btn">
                  Assign
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Outline builder (AI generate / upload & parse) */}
          {outlineBuilderVolume && (
            <OutlineBuilder
              pursuitId={pursuitId}
              volumeId={outlineBuilderVolume.id}
              volumeLabel={outlineBuilderVolume.label}
              open={!!outlineBuilderVolume}
              onOpenChange={(v) => !v && setOutlineBuilderVolume(null)}
              onCommitted={() => {
                setOutlineBuilderVolume(null);
                fetchStructure();
              }}
            />
          )}

          {/* Volume-scoped reviews — Cycle drives the ladder, Schedule books them */}
          <Dialog open={!!reviewPanelVolume} onOpenChange={(v) => !v && setReviewPanelVolume(null)}>
            <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-pink-400" />
                  Reviews — {reviewPanelVolume?.label}
                </DialogTitle>
              </DialogHeader>
              {reviewPanelVolume && (
                <Tabs defaultValue="cycle" className="w-full">
                  <TabsList className="bg-slate-800 border border-slate-700">
                    <TabsTrigger value="cycle" className="text-xs data-[state=active]:bg-slate-700">Cycle</TabsTrigger>
                    <TabsTrigger value="schedule" className="text-xs data-[state=active]:bg-slate-700">Schedule</TabsTrigger>
                  </TabsList>
                  <TabsContent value="cycle" className="mt-4">
                    <VolumeReviewCycle
                      pursuitId={pursuitId}
                      volumeId={reviewPanelVolume.id}
                      volumeLabel={reviewPanelVolume.label}
                      onJumpToSection={jumpToSection}
                    />
                  </TabsContent>
                  <TabsContent value="schedule" className="mt-4">
                    <VolumeReviewPanel
                      pursuitId={pursuitId}
                      volumeId={reviewPanelVolume.id}
                      sections={reviewPanelVolume.sections}
                    />
                  </TabsContent>
                </Tabs>
              )}
            </DialogContent>
          </Dialog>

          {/* Progress Bar */}
          <div className="max-w-7xl mx-auto mt-4 flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                <span>{stats.sectionsWithContent} of {stats.totalSections} sections started</span>
                <span>{stats.progress}% complete</span>
              </div>
              <Progress value={stats.progress} className="h-2 bg-slate-800" />
            </div>
            <Badge variant="outline" className="border-slate-700 text-slate-300">
              {stats.totalRequirements} Requirements
            </Badge>
          </div>
        </div>

        <div className="px-8 bg-slate-950">
          <div className="max-w-7xl mx-auto">
            <PursuitLifecycleStepper />
          </div>
        </div>

        {/* v3.18.20 — Workspace flex panes inset by max-w-7xl + px-8 so the
            left sidebar (Formatting Lock + Vol I/II/III/IV) gets a proper
            left gutter consistent with the rest of the pursuit screens. */}
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex h-[calc(100vh-160px)] border-x border-slate-800">
          {/* Left Sidebar - Tree View */}
          <div className="w-80 border-r border-slate-800 bg-slate-900/50 overflow-y-auto">
            {/* Formatting Lock Panel */}
            {structure?.formatting_lock && (
              <div className="p-3 border-b border-slate-800 bg-slate-900">
                <div className="flex items-center gap-2 text-xs font-medium text-slate-400 mb-2">
                  <Layout className="w-3 h-3" />
                  FORMATTING LOCK
                </div>
                <div className="space-y-1 text-xs text-slate-500">
                  {structure.formatting_lock.font && (
                    <div className="flex items-center gap-2">
                      <Type className="w-3 h-3" />
                      <span className="truncate">{structure.formatting_lock.font}</span>
                    </div>
                  )}
                  {structure.formatting_lock.margins && (
                    <div className="flex items-center gap-2">
                      <Layout className="w-3 h-3" />
                      <span className="truncate">{structure.formatting_lock.margins}</span>
                    </div>
                  )}
                  {structure.formatting_lock.line_spacing && (
                    <div className="flex items-center gap-2">
                      <FileText className="w-3 h-3" />
                      <span className="truncate">{structure.formatting_lock.line_spacing}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Volume Tree */}
            <div className="p-2">
              {structure && Object.entries(structure.volumes || {}).map(([volNum, volume]) => {
                const config = VOLUME_CONFIG[volNum] || VOLUME_CONFIG['I'];
                const isExpanded = expandedVolumes[volNum];
                // Real Proposal Architect volumes (id-keyed) support Book Boss,
                // outline creation, and Pink/Red reviews; the legacy hardcoded
                // I/II/III/IV fallback (no real backend volume to attach to)
                // doesn't — those pursuits just see the tree as before.
                const isArchitectVolume = structure.source === 'proposal_architect';

                return (
                  <Collapsible
                    key={volNum}
                    open={isExpanded}
                    onOpenChange={() => toggleVolume(volNum)}
                    className="mb-1"
                  >
                    <div
                      className={cn(
                        "w-full flex items-center gap-2 p-2 rounded-lg transition-colors",
                        "hover:bg-slate-800/50",
                        isExpanded && "bg-slate-800/30"
                      )}
                    >
                      <CollapsibleTrigger asChild>
                        <button
                          className="flex items-center gap-2 flex-1 min-w-0 text-left"
                          data-testid={`volume-toggle-${volNum}`}
                        >
                          {isExpanded ? (
                            <FolderOpen className={`w-4 h-4 text-${config.color}-400 flex-shrink-0`} />
                          ) : (
                            <Folder className={`w-4 h-4 text-${config.color}-400 flex-shrink-0`} />
                          )}
                          <span className="flex-1 min-w-0 font-medium text-sm text-slate-200 truncate">
                            Vol {volume.number}: {volume.title}
                          </span>
                          {volume.page_limit && (
                            <span className="text-xs text-slate-500 flex-shrink-0">{volume.page_limit}pg</span>
                          )}
                        </button>
                      </CollapsibleTrigger>
                      {isArchitectVolume && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBookBossDraft({ name: volume.book_boss_name || '', email: volume.book_boss_email || '' });
                                  setBookBossDialogVolume({ id: volNum, label: volume.title });
                                }}
                                className={cn(
                                  "text-[10px] px-1.5 py-0.5 rounded border",
                                  volume.book_boss_name
                                    ? "text-violet-300 border-violet-500/40 bg-violet-500/10"
                                    : "text-slate-500 border-slate-700 hover:bg-slate-800"
                                )}
                                data-testid={`book-boss-btn-${volNum}`}
                              >
                                <UserCog className="w-3 h-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              {volume.book_boss_name ? `Book Boss: ${volume.book_boss_name}` : 'Assign Book Boss'}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReviewPanelVolume({ id: volNum, label: volume.title, sections: volume.sections || [] });
                                }}
                                className="text-[10px] px-1.5 py-0.5 rounded border text-slate-500 border-slate-700 hover:bg-slate-800"
                                data-testid={`volume-reviews-btn-${volNum}`}
                              >
                                <CalendarClock className="w-3 h-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">Pink/Red Team Reviews</TooltipContent>
                          </Tooltip>
                        </div>
                      )}
                      <CollapsibleTrigger asChild>
                        <button data-testid={`volume-expand-${volNum}`}>
                          <ChevronDown className={cn(
                            "w-4 h-4 text-slate-500 transition-transform",
                            isExpanded && "rotate-180"
                          )} />
                        </button>
                      </CollapsibleTrigger>
                    </div>

                    <CollapsibleContent className="ml-4 mt-1 space-y-0.5">
                      {volume.sections?.length > 0 ? (
                        volume.sections.map((section) => {
                          const statusConfig = STATUS_CONFIG[section.status] || STATUS_CONFIG['NOT_STARTED'];
                          const isSelected = selectedSection?.section_id === section.section_id;
                          const roles = roleAssignments[section.section_id] || {};

                          return (
                            <div
                              key={section.section_id}
                              className={cn(
                                "w-full rounded-md text-sm",
                                isSelected
                                  ? "bg-blue-600/20 border border-blue-500/50"
                                  : "hover:bg-slate-800/50 border border-transparent"
                              )}
                            >
                              <button
                                onClick={() => handleSelectSection(volume, section)}
                                className="w-full flex items-center gap-2 p-2 text-left"
                                data-testid={`section-btn-${section.section_id}`}
                              >
                                <statusConfig.icon className={cn(
                                  "w-3.5 h-3.5 flex-shrink-0",
                                  section.status === 'COMPLIANT' ? 'text-emerald-400' :
                                  section.status === 'IN_REVIEW' ? 'text-amber-400' :
                                  section.status === 'DRAFTING' ? 'text-blue-400' : 'text-slate-500'
                                )} />
                                <div className="flex-1 min-w-0">
                                  <p className={cn(
                                    "truncate",
                                    isSelected ? "text-blue-200" : "text-slate-300"
                                  )}>
                                    {section.section_title}
                                  </p>
                                  <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <span>{section.requirement_count} req</span>
                                    {section.word_count > 0 && (
                                      <span>{section.word_count} words</span>
                                    )}
                                  </div>
                                </div>
                              </button>
                              {isArchitectVolume && (
                                <div className="flex items-center gap-1 px-2 pb-1.5">
                                  <SectionRoleAssigner
                                    pursuitId={pursuitId}
                                    sectionId={section.section_id}
                                    role="WRITER"
                                    assignment={roles.WRITER}
                                    onChanged={(a) => setRoleAssignments((prev) => ({
                                      ...prev,
                                      [section.section_id]: { ...prev[section.section_id], WRITER: a || undefined },
                                    }))}
                                  />
                                  <SectionRoleAssigner
                                    pursuitId={pursuitId}
                                    sectionId={section.section_id}
                                    role="REVIEWER"
                                    assignment={roles.REVIEWER}
                                    onChanged={(a) => setRoleAssignments((prev) => ({
                                      ...prev,
                                      [section.section_id]: { ...prev[section.section_id], REVIEWER: a || undefined },
                                    }))}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : isArchitectVolume ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full h-8 text-xs border-slate-700 text-slate-300 hover:bg-slate-800 m-1"
                          onClick={() => setOutlineBuilderVolume({ id: volNum, label: volume.title })}
                          data-testid={`build-outline-btn-${volNum}`}
                        >
                          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                          Build Outline
                        </Button>
                      ) : (
                        <p className="text-xs text-slate-500 italic p-2">
                          No sections extracted
                        </p>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
              
              {(!structure || Object.keys(structure.volumes || {}).length === 0) && (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No structure available</p>
                  <p className="text-xs mt-1">Upload an RFP to populate</p>
                </div>
              )}
            </div>
          </div>

          {/* Main Editor Area */}
          <div className="flex-1 flex flex-col bg-slate-950">
            {selectedSection ? (
              <>
                {/* Editor Header */}
                <div className="bg-slate-900 border-b border-slate-800 px-6 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="font-semibold text-white">
                          {selectedSection.section_title}
                        </h2>
                        <Badge className={STATUS_CONFIG[selectedSection.status]?.color || 'bg-slate-700'}>
                          {STATUS_CONFIG[selectedSection.status]?.label || 'Unknown'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-400 mt-1">
                        Volume {selectedVolume.number}: {selectedVolume.title} • {selectedSection.requirement_count} requirements
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Status Selector */}
                      <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
                        {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                          <Tooltip key={status}>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => updateDraftStatus(selectedSection.section_id, status)}
                                className={cn(
                                  "p-1.5 rounded transition-colors",
                                  selectedSection.status === status 
                                    ? config.color 
                                    : "text-slate-500 hover:text-slate-300"
                                )}
                              >
                                <config.icon className="w-4 h-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>{config.label}</TooltipContent>
                          </Tooltip>
                        ))}
                      </div>
                      
                      <span className="text-sm text-slate-400 px-2">
                        {wordCount} words
                      </span>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleManualSave}
                        disabled={isSaving}
                        className="border-slate-700 text-slate-300 hover:bg-slate-800"
                        data-testid="manual-save-btn"
                      >
                        {isSaving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Editor with Sidebars */}
                <div className="flex-1 flex overflow-hidden">
                  {/* Main Editor */}
                  <div className="flex-1 p-6 overflow-y-auto">
                    <div className="max-w-3xl mx-auto">
                      <Textarea
                        ref={editorRef}
                        value={editorContent}
                        onChange={(e) => handleEditorChange(e.target.value)}
                        placeholder={`Start writing ${selectedSection.section_title}...\n\nUse the panels on the right to access:\n• Requirement Anchors (Shall Statements)\n• Draft Shells (Ghost Text)\n• Win Themes from Past Performance`}
                        className="min-h-[500px] resize-none bg-slate-900 border-slate-700 text-slate-100 font-mono text-sm leading-relaxed placeholder:text-slate-600"
                        data-testid="proposal-editor"
                      />
                      
                      {/* Auto-save indicator */}
                      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          <span>Auto-saves every 30 seconds</span>
                        </div>
                        {lastSaved && (
                          <span className="text-emerald-500">
                            Saved: {new Date(lastSaved).toLocaleTimeString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Sidebar - Architect Tools */}
                  <div className="w-80 border-l border-slate-800 bg-slate-900/50 overflow-y-auto">
                    {/* v3.18.2 — Themes Threading (Win Theme Registry tags per section) */}
                    <SectionThemeTagger
                      pursuitId={pursuitId}
                      sectionId={selectedSection.section_id}
                    />

                    {/* Desktop-upload document versions — real Proposal
                        Architect outline sections only (legacy synthesized
                        section_ids have no pursuit_outline_sections row to
                        attach uploads to). */}
                    {structure?.source === 'proposal_architect' && (
                      <Collapsible open={showDocVersions} onOpenChange={setShowDocVersions}>
                        <CollapsibleTrigger className="w-full p-3 flex items-center justify-between border-b border-slate-800 hover:bg-slate-800/50">
                          <div className="flex items-center gap-2">
                            <Upload className="w-4 h-4 text-emerald-400" />
                            <span className="font-medium text-sm">Document Versions</span>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="p-3 border-b border-slate-800">
                          <SectionDocumentVersions pursuitId={pursuitId} sectionId={selectedSection.section_id} />
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {/* Requirements Panel */}
                    <Collapsible open={showRequirements} onOpenChange={setShowRequirements}>
                      <CollapsibleTrigger className="w-full p-3 flex items-center justify-between border-b border-slate-800 hover:bg-slate-800/50">
                        <div className="flex items-center gap-2">
                          <ListChecks className="w-4 h-4 text-blue-400" />
                          <span className="font-medium text-sm">Requirement Anchors</span>
                        </div>
                        <Badge variant="outline" className="border-slate-700 text-xs">
                          {selectedSection.requirements?.length || 0}
                        </Badge>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="p-3 space-y-2 border-b border-slate-800 max-h-64 overflow-y-auto">
                        {selectedSection.requirements?.length > 0 ? (
                          selectedSection.requirements.map((req, idx) => (
                            <Tooltip key={req.id}>
                              <TooltipTrigger asChild>
                                <div 
                                  className="p-2 bg-slate-800/50 rounded-md text-xs cursor-pointer hover:bg-slate-800 transition-colors"
                                  onClick={() => insertGhostText(`[Ref: ${req.requirement_number}] `)}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-medium text-blue-300">
                                      {req.requirement_number || `Req ${idx + 1}`}
                                    </span>
                                    <Badge className={cn(
                                      "text-xs",
                                      req.requirement_type === 'MANDATORY' ? 'bg-red-900/50 text-red-300' : 'bg-slate-700'
                                    )}>
                                      {req.requirement_type}
                                    </Badge>
                                  </div>
                                  <p className="text-slate-400 line-clamp-2">
                                    {req.requirement_text}
                                  </p>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-xs">
                                <p className="text-xs">{req.requirement_text}</p>
                                <p className="text-xs text-slate-400 mt-1">Click to insert reference</p>
                              </TooltipContent>
                            </Tooltip>
                          ))
                        ) : (
                          <p className="text-xs text-slate-500 italic">
                            No requirements for this section
                          </p>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                    
                    {/* Draft Shells Panel */}
                    <Collapsible defaultOpen>
                      <CollapsibleTrigger className="w-full p-3 flex items-center justify-between border-b border-slate-800 hover:bg-slate-800/50">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-amber-400" />
                          <span className="font-medium text-sm">Draft Shells</span>
                        </div>
                        <Badge variant="outline" className="border-slate-700 text-xs">
                          Ghost Text
                        </Badge>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="p-3 space-y-2 border-b border-slate-800 max-h-64 overflow-y-auto">
                        {draftShells.length > 0 ? (
                          draftShells.map((shell, idx) => (
                            <div 
                              key={shell.requirement_id}
                              className="group relative"
                            >
                              <div className="p-2 bg-amber-900/20 border border-amber-700/30 rounded-md text-xs">
                                <p className="text-amber-200/60 italic mb-2">
                                  "{shell.ghost_text}"
                                </p>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500 text-xs">
                                    For: {shell.requirement_number}
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2 text-xs text-amber-400 hover:text-amber-300 hover:bg-amber-900/30"
                                    onClick={() => insertGhostText(shell.ghost_text)}
                                  >
                                    Insert
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-slate-500 italic">
                            No draft shells available
                          </p>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                    
                    {/* Win Themes Panel */}
                    <Collapsible open={showWinThemes} onOpenChange={setShowWinThemes}>
                      <CollapsibleTrigger className="w-full p-3 flex items-center justify-between hover:bg-slate-800/50">
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-emerald-400" />
                          <span className="font-medium text-sm">Win Themes</span>
                        </div>
                        <Badge variant="outline" className="border-slate-700 text-xs">
                          Past Perf
                        </Badge>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="p-3 space-y-2 max-h-64 overflow-y-auto">
                        {winThemes.length > 0 ? (
                          winThemes.map((theme, idx) => (
                            <div 
                              key={theme.id}
                              className="p-3 bg-emerald-900/20 border border-emerald-700/30 rounded-md"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm text-emerald-300 truncate flex-1">
                                  {theme.contract_title}
                                </span>
                                <Badge className="bg-emerald-900/50 text-emerald-300 text-xs">
                                  Score: {theme.relevance_score}
                                </Badge>
                              </div>
                              <p className="text-xs text-slate-400 mb-2">
                                {theme.agency} • ${(theme.contract_value / 1000000).toFixed(1)}M
                              </p>
                              <p className="text-xs text-emerald-200/60 italic mb-2">
                                "{theme.suggested_text}"
                              </p>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="w-full h-7 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30"
                                onClick={() => insertGhostText(theme.suggested_text)}
                              >
                                <Lightbulb className="w-3 h-3 mr-1" />
                                Use Win Theme
                              </Button>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4">
                            <Award className="w-6 h-6 text-slate-600 mx-auto mb-2" />
                            <p className="text-xs text-slate-500">
                              No matching past performance found
                            </p>
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                    
                    {/* Source Recommendations Panel (Similarity Matches) */}
                    <Collapsible open={showRecommendations} onOpenChange={setShowRecommendations}>
                      <CollapsibleTrigger className="w-full p-3 flex items-center justify-between hover:bg-slate-800/50">
                        <div className="flex items-center gap-2">
                          <Lightbulb className="w-4 h-4 text-purple-400" />
                          <span className="font-medium text-sm">Source Recommendations</span>
                        </div>
                        {sourceRecommendations.length > 0 && (
                          <Badge variant="outline" className="border-purple-700/50 text-purple-300 text-xs">
                            {sourceRecommendations.length} matches
                          </Badge>
                        )}
                      </CollapsibleTrigger>
                      <CollapsibleContent className="p-3 space-y-2 max-h-80 overflow-y-auto">
                        {sourceRecommendations.length > 0 ? (
                          sourceRecommendations.map((rec, idx) => (
                            <div 
                              key={rec.chunk_id || idx}
                              className="p-3 bg-purple-900/20 border border-purple-700/30 rounded-md group"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-purple-300 font-medium truncate flex-1">
                                  From: {rec.source_document_name || 'Winning Proposal'}
                                </span>
                                <Badge className="bg-purple-900/50 text-purple-200 text-[10px]">
                                  {Math.round((rec.similarity_score || 0) * 100)}% match
                                </Badge>
                              </div>
                              <p className="text-xs text-slate-300 mb-2 line-clamp-3">
                                "{rec.recommended_text}"
                              </p>
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="flex-1 h-6 text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-900/30"
                                  onClick={() => insertGhostText(rec.recommended_text)}
                                >
                                  <Edit3 className="w-3 h-3 mr-1" />
                                  Insert
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-xs text-slate-400 hover:text-white hover:bg-slate-700"
                                  onClick={() => {
                                    navigator.clipboard.writeText(rec.recommended_text);
                                    toast.success('Copied to clipboard');
                                  }}
                                >
                                  Copy
                                </Button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4">
                            <Lightbulb className="w-6 h-6 text-slate-600 mx-auto mb-2" />
                            <p className="text-xs text-slate-500">
                              No similarity matches found.
                            </p>
                            <p className="text-xs text-slate-600 mt-1">
                              Run AI Analysis to find matches from Winning Proposals.
                            </p>
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                </div>
              </>
            ) : (
              // No section selected
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center max-w-md">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600/20 to-purple-600/20 flex items-center justify-center mx-auto mb-6">
                    <FileText className="w-10 h-10 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    Select a Section to Edit
                  </h3>
                  <p className="text-slate-400 mb-6">
                    The Proposal Architect has organized your RFP into volumes and sections 
                    with requirements anchored to each. Select a section from the tree to 
                    start drafting with AI assistance.
                  </p>
                  <div className="grid grid-cols-4 gap-3 text-xs">
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <ListChecks className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                      <p className="text-slate-400">Requirements</p>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <Sparkles className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                      <p className="text-slate-400">Draft Shells</p>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <Award className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
                      <p className="text-slate-400">Win Themes</p>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <Lightbulb className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                      <p className="text-slate-400">Similarity</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Navigation — v3.18.13 aligned with new lifecycle.
            v3.18.16: Back → Compliance Matrix (the planning artifact that
            drove this draft); Forward → Submission (final format & delivery). */}
        <div className="fixed bottom-0 left-64 right-0 bg-slate-900 border-t border-slate-800 px-6 py-3 z-40">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <Button 
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/compliance`)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Compliance Matrix
            </Button>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={() => navigate(`/${workspaceId}/pipeline`)}
              >
                View Pipeline
              </Button>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/compliance?tab=submission-day`)}
              >
                <FileText className="w-4 h-4 mr-2" />
                Proceed to Submission
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
